import { InferGetServerSidePropsType } from 'next';
import { useIntl } from 'react-intl';
import { BreadcrumbList, Person, WebPage, WithContext } from 'schema-dts';

import { Locale } from '../../../../../core/i18n/Locale';
import { Site } from '../../../../../core/Site/Site';
import { useTrackPageViewAmplitude } from '../../../../../core/Tracking/Amplitude/Amplitude';
import { BreadcrumbItem } from '../../../../../design-system/components/breadcrumb/Breadcrumb';
import {
    Breadcrumb,
    useDestinationPracticalInformationArticleBreadcrumbItems,
} from '../../../../../domains/Layout/Breadcrumb/Breadcrumb';
import { Footer } from '../../../../../domains/Layout/Footer/Footer';
import { Head } from '../../../../../domains/Layout/Head/Head';
import { useJsonLd } from '../../../../../domains/Layout/Head/JsonLd';
import { getAuthorJsonLd } from '../../../../../domains/Layout/Head/utilities/schemaGenerators';
import { imageLoaderForId } from '../../../../../domains/Layout/Image/URL/ImageURL';
import { PrimaryNav } from '../../../../../domains/Layout/PrimaryNav/PrimaryNav';
import { DestinationSuitcaseInspirationLink } from '../../../../../domains/TravelGuide/Article/DestinationSuitcaseInspirationLink';
import { DestinationVocabularyInspirationLink } from '../../../../../domains/TravelGuide/Article/DestinationVocabularyInspirationLink';
import { DestinationPracticalInformationArticle } from '../../../../../partials/destination-practical-information-article/Article/DestinationPracticalInformationArticle';
import { getServerSideProps } from '../../../../../partials/destination-practical-information-article/getServerSideProps';
import { Author } from '../../../../../resources/author/domain/types/author';
import { Destination } from '../../../../../resources/destination/domain/types/destination';
import { DestinationArticle } from '../../../../../resources/destinationArticles/domain/types/destinationArticle';
import { AuthorBannerSection } from '../../../../../shared/components/AuthorBannerSection/AuthorBannerSection';
import { DestinationArticlesCarouselSection } from '../../../../../shared/components/DestinationArticlesCarouselSection/DestinationArticlesCarouselSection';
import { ItineraryListCarouselSection } from '../../../../../shared/components/ItineraryListCarouselSection/ItineraryListCarouselSection';
import { useLocale } from '../../../../../shared/hooks/useLocale';
import { useUrlGenerator } from '../../../../../shared/hooks/useUrlGenerator';
import { useCurrentSite } from '../../../../../shared/providers/currentSite/useCurrentSite';
import { htmlToText } from '../../../../../shared/utilities/htmlToText';
import { generateUrl } from '../../../../../shared/utilities/routing/routing';
import { PickDomain } from '../../../../../shared/utilities/types/PickDomain';

export { getServerSideProps };

type PageDestinationPracticalInformationProps = InferGetServerSidePropsType<
    typeof getServerSideProps
>;

export default function PageDestinationPracticalInformation(
    props: PageDestinationPracticalInformationProps,
) {
    const { currentSite } = useCurrentSite();
    const { generateUrl } = useUrlGenerator();
    const { locale } = useLocale();
    const { formatMessage } = useIntl();

    const {
        destinationPracticalInformationArticle,
        itineraries,
        destinationItinerariesCount,
        hasDestinationSuitcase,
        hasDestinationVocabulary,
        otherDestinationPracticalInformationArticles,
        author,
        primaryNav,
        hrefLangs,
    } = props.data;

    const { destination } = destinationPracticalInformationArticle;

    const { breadcrumbItems } = useDestinationPracticalInformationArticleBreadcrumbItems({
        destination,
        destinationPracticalInformation: destinationPracticalInformationArticle,
    });

    const destinationPracticalInformationUrl = generateUrl(
        'destination_practical_information_article',
        {
            destinationSlug: destination.slug,
            articleDocumentId: destinationPracticalInformationArticle.documentId,
            articleSlug: destinationPracticalInformationArticle.slug,
        },
    );

    const metaTitle = formatMessage(
        {
            id: 'page.destinationPracticalInformation.meta.title',
        },
        {
            destinationName: destination.name,
            title: destinationPracticalInformationArticle.shortTitle,
        },
    );

    const metaDescription = htmlToText(destinationPracticalInformationArticle.summary);

    const meta = getMetaData({
        destination,
        locale,
        author,
        destinationPracticalInformationArticle,
        metaTitle,
        metaDescription,
        currentSite,
        breadcrumbItems,
    });

    useTrackPageViewAmplitude((ampli, defaultProperties) =>
        ampli.viewedPageDestinationArticle({
            ...defaultProperties,
            destination_id: destination.id,
            destination_slug: destination.slug,
            has_trip_form_available: destination.isAvailableForQuoteRequest,
            article_type: destinationPracticalInformationArticle.type,
        }),
    );

    return (
        <>
            <Head
                title={metaTitle}
                description={metaDescription}
                canonical={destinationPracticalInformationUrl}
                hrefLangs={hrefLangs}
                og={{
                    title: metaTitle,
                    description: metaDescription,
                    image: imageLoaderForId({
                        aspectRatio: 1.91,
                    })({
                        src: destination.coverPicture.id.toString(),
                        width: 1200,
                    }),
                    locale,
                    url: destinationPracticalInformationUrl,
                    type: 'article',
                    site_name: currentSite.organisation.name,
                }}
                jsonLd={useJsonLd(meta.jsonLd)}
            />

            <PrimaryNav contextContinent={destination.continent} {...primaryNav} />

            <div className="layout-with-centered-content mb-48">
                <section className="main-container">
                    <Breadcrumb items={breadcrumbItems} />
                </section>
            </div>

            <div className="layout-with-centered-content mb-40 last:mb-0 md:mb-96">
                <section className="main-container">
                    <DestinationPracticalInformationArticle
                        article={destinationPracticalInformationArticle}
                        destination={destination}
                        author={author}
                    />
                </section>
            </div>

            {author ? (
                <div className="layout-with-centered-content mb-40 last:mb-0 md:mb-96">
                    <section className="main-container">
                        <AuthorBannerSection author={author} />
                    </section>
                </div>
            ) : null}

            {itineraries.length > 0 ? (
                <div className="layout-with-centered-content mb-40 last:mb-0 md:mb-96">
                    <section className="main-container">
                        <ItineraryListCarouselSection
                            destination={destination}
                            itineraries={itineraries}
                            destinationItinerariesCount={destinationItinerariesCount}
                            itineraryCardTitleElement="div"
                        />
                    </section>
                </div>
            ) : null}

            {otherDestinationPracticalInformationArticles.length > 0 ? (
                <div className="layout-with-centered-content mb-40 last:mb-0 md:mb-96">
                    <section className="main-container">
                        <DestinationArticlesCarouselSection
                            destination={destination}
                            articles={otherDestinationPracticalInformationArticles}
                        />
                    </section>
                </div>
            ) : null}

            {hasDestinationSuitcase || hasDestinationVocabulary ? (
                <div className="layout-with-centered-content mb-40 last:mb-0 md:mb-96">
                    <section className="main-container">
                        <div className="row">
                            {hasDestinationSuitcase ? (
                                <div className="col-12 col-lg-6 mb-24 lg:mb-0">
                                    <DestinationSuitcaseInspirationLink destination={destination} />
                                </div>
                            ) : null}
                            {hasDestinationVocabulary ? (
                                <div className="col-12 col-lg-6">
                                    <DestinationVocabularyInspirationLink
                                        destination={destination}
                                    />
                                </div>
                            ) : null}
                        </div>
                    </section>
                </div>
            ) : null}

            <Footer
                destination={destination}
                topDestinations={primaryNav.destinationsMenu?.world.topDestinations ?? []}
            />
        </>
    );
}

function getMetaData({
    destination,
    locale,
    author,
    metaTitle,
    metaDescription,
    currentSite,
    destinationPracticalInformationArticle,
    breadcrumbItems,
}: {
    destination: PickDomain<Destination, 'name' | 'slug' | 'coverPicture' | 'preposition'>;
    locale: Locale;
    author: PickDomain<
        Author,
        'id' | 'firstName' | 'lastName' | 'role' | 'description' | 'picture' | 'slug',
        {
            linkedinUrl: Author['linkedinUrl'] | null;
            instagramUrl: Author['instagramUrl'] | null;
        }
    > | null;
    metaTitle: string;
    metaDescription: string;
    currentSite: Site;
    destinationPracticalInformationArticle: PickDomain<
        DestinationArticle,
        'content' | 'updatedAt' | 'documentId' | 'slug' | 'title' | 'publishedAt'
    >;
    breadcrumbItems: Array<BreadcrumbItem>;
}) {
    const headline = destinationPracticalInformationArticle.title;

    const webPageJsonLd = getWebPageJsonLd({
        locale,
        destination,
        metaDescription,
        currentSite,
        headline,
        destinationPracticalInformationArticle,
        author,
        breadcrumbItems,
    });

    return {
        title: metaTitle,
        description: metaDescription,
        jsonLd: [webPageJsonLd],
    };
}

function getWebPageJsonLd({
    locale,
    destination,
    metaDescription,
    currentSite,
    headline,
    destinationPracticalInformationArticle,
    author,
    breadcrumbItems,
}: {
    locale: Locale;
    destination: PickDomain<Destination, 'name' | 'slug' | 'coverPicture'>;
    metaDescription: string;
    headline: string;
    currentSite: Site;
    destinationPracticalInformationArticle: PickDomain<
        DestinationArticle,
        'content' | 'updatedAt' | 'documentId' | 'slug' | 'publishedAt'
    >;
    author: PickDomain<
        Author,
        'id' | 'firstName' | 'lastName' | 'role' | 'description' | 'picture' | 'slug',
        {
            linkedinUrl: Author['linkedinUrl'] | null;
            instagramUrl: Author['instagramUrl'] | null;
        }
    > | null;
    breadcrumbItems: Array<BreadcrumbItem>;
}): WithContext<WebPage> | null {
    const destinationPracticalInformationArticlePageUrl = generateUrl(
        'destination_practical_information_article',
        locale,
        {
            destinationSlug: destination.slug,
            articleDocumentId: destinationPracticalInformationArticle.documentId,
            articleSlug: destinationPracticalInformationArticle.slug,
        },
    );
    const articleIdUrl = `${destinationPracticalInformationArticlePageUrl}#article`;

    const authorEntity: WithContext<Person> | null = author
        ? getAuthorJsonLd({
              author,
              locale,
              currentSite,
          })
        : null;

    const mainEntity: WithContext<WebPage>['mainEntity'] = {
        '@type': 'Article',
        '@id': articleIdUrl,
        headline,
        url: destinationPracticalInformationArticlePageUrl,
        articleBody: htmlToText(destinationPracticalInformationArticle.content),
        mainEntityOfPage: destinationPracticalInformationArticlePageUrl,
        ...(authorEntity ? { author: authorEntity } : {}),
    };

    const breadcrumbList: BreadcrumbList = {
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbItems.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.text,
            item: item.url,
        })),
    };

    return {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${destinationPracticalInformationArticlePageUrl}#webpage`,
        url: destinationPracticalInformationArticlePageUrl,
        inLanguage: locale,
        headline,
        description: metaDescription,
        mainEntity,
        breadcrumb: breadcrumbList,
        ...(destinationPracticalInformationArticle.updatedAt
            ? { dateModified: destinationPracticalInformationArticle.updatedAt }
            : {}),
        ...(destinationPracticalInformationArticle.publishedAt
            ? { datePublished: destinationPracticalInformationArticle.publishedAt }
            : {}),
    };
}
